





























































































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { CollabLiveChat } from "@scrile/api-provider/dist/api/CollabLiveChatsProvider";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import { BlockingStatus, MediaConstraints, MenuItem } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import BaseVideo from "@/components/BaseVideo";
import BlockUserController from "@/components/BlockUserController";
import SiteLogo from "@/components/SiteLogo";
import useTimePassed from "@/views/PageProfile/components/StreamControllers/hooks/useTimePassed";
import UserStreamsView from "./components/UserStreamsView.vue";
import { connectionStateEnum } from "../../const";
import { StreamUser } from "../../types";
import ControlButtons from "../ControlButtons";
import StreamChat from "../StreamChat";
import StreamTipsController from "../StreamTipsController";
import StreamViewersController from "../StreamViewersController";
import UserMutedConfirm from "../UserMutedConfirm";

export default defineComponent({
  name: "StreamView",
  components: {
    UserStreamsView,
    BaseIconButton,
    BaseMenu,
    SiteLogo,
    ControlButtons,
    StreamTipsController,
    StreamViewersController,
    UserMutedConfirm,
    BlockUserController,
    BaseVideo,
    BaseButton,
    BaseIcon,
    StreamChat,
  },
  props: {
    loading: { type: Boolean, required: true },
    isOwner: { type: Boolean, required: true },
    produce: { type: Boolean, required: true },
    localStream: { type: MediaStream },
    streamers: { type: Array as PropType<StreamUser[]>, required: true },
    producer: { type: Object as PropType<UserPublicData>, required: true },
    me: { type: Object as PropType<LiveChatUser> },
    connecting: { type: Boolean, required: true },
    connectingState: { type: String, required: true },
    userList: { type: Array as () => LiveChatUser[], required: true },
    streamStartedDate: { type: String, required: true },
    threadId: { type: String },
    token: { type: String },
    sessionId: { type: String },
    constraints: { type: Object as PropType<MediaConstraints> },
    collabLive: { type: Object as PropType<CollabLiveChat> },
    showEnding: { type: Boolean, default: false },
    showBlockedWarning: { type: Boolean, default: false },
    menuItems: { type: Array as PropType<MenuItem[]>, default: () => [] },
    referrersMap: { type: Object as PropType<Record<string, UserPublicData>> },
  },
  setup(props, { emit }) {
    const { timePassed } = useTimePassed(computed(() => props.streamStartedDate));
    const streamChat = ref();
    const showTime = ref(true);
    const showMutedConfirm = ref(false);
    const blockProcessing = ref(false);
    const blockingStatus = ref<BlockingStatus>({
      authUser: false,
      opponent: false,
    });
    const selectedUserToBlock = ref<LiveChatUser | null>(null);

    const opponentUser = computed(() => (props.produce ? selectedUserToBlock.value?.user : props.producer));

    const blockConfirmText = computed(() =>
      $t("Are you sure you want to block {0}?", [
        selectedUserToBlock.value?.user
          ? selectedUserToBlock.value?.user.screenName
          : `Guest user ${selectedUserToBlock.value?.id}`,
      ])
    );

    const isBlocked = computed(() => blockingStatus.value.authUser || props.me?.blocked || props.showBlockedWarning);
    const blockedWarningText = computed(() => $t("You have been blocked by {0}", [props.producer?.screenName]));

    const timeClick = () => (showTime.value = !showTime.value);

    const overlayClass = computed(() => ({
      __background: props.showEnding || props.loading || props.connecting,
    }));

    const showChat = computed(
      () => !props.connecting && !props.loading && !!props.threadId && !!props.token && !isBlocked.value
    );

    const moderatorIds = computed(() => props.collabLive?.moderatorIds ?? []);
    const streamerUserIds = computed(() =>
      props.collabLive ? props.collabLive.broadcasterUserIds : [props.producer.id]
    );

    const showConnecting = computed(
      () =>
        !props.showEnding &&
        (props.loading || props.connecting || props.connectingState === connectionStateEnum.CONNECTING)
    );
    const showDisconnect = computed(
      () => !props.showEnding && props.connectingState === connectionStateEnum.DISCONNECTED
    );
    const showOverlay = computed(
      () => props.showEnding || showConnecting.value || showDisconnect.value || props.showEnding || isBlocked.value
    );

    const handleBlockForSession = (userId?: string) => {
      const liveChatUserId = selectedUserToBlock.value?.id || userId;
      if (!liveChatUserId) return;
      blockProcessing.value = true;
      emit("blockForSession", {
        chatUserId: liveChatUserId,
        cb: () => {
          selectedUserToBlock.value = null;
        },
        loadingCb: () => {
          blockProcessing.value = false;
        },
      });
    };

    const onBlockingStatusUpdated = ({ user, isBlocked }: { user: UserPublicData; isBlocked: boolean }) => {
      if (props.collabLive && isBlocked) {
        const liveChatUser = props.userList.find(liveUser => liveUser.userId === user.id);
        handleBlockForSession(liveChatUser?.id);
      }
    };

    const onClose = () => emit("close");
    const endClick = () => {
      if (props.collabLive) {
        return emit("click:endChat");
      } else if (props.produce) {
        emit("triggerShowEnding");
      } else {
        onClose();
      }
    };

    watch(
      blockingStatus,
      () => {
        if (blockingStatus.value.authUser) {
          emit("blockedCloseStream");
        }
      },
      { immediate: true }
    );

    watch(toRef(props, "me"), value => {
      if (value && value.muted) {
        const baseSendMessage = streamChat.value.$refs.baseSendMessage;
        if (!!baseSendMessage) {
          const txtArea = getElement(baseSendMessage).querySelector("textarea");
          txtArea && txtArea.blur();
        }
        showMutedConfirm.value = true;
      }
      if (value && value.blocked) {
        emit("blockedCloseStream");
      }
    });

    return {
      streamChat,
      showTime,
      showMutedConfirm,
      showChat,
      timePassed,
      overlayClass,
      connectionStateEnum,
      blockingStatus,
      blockedWarningText,
      selectedUserToBlock,
      isBlocked,
      opponentUser,
      blockConfirmText,
      showConnecting,
      showDisconnect,
      showOverlay,
      moderatorIds,
      streamerUserIds,
      blockProcessing,
      onBlockingStatusUpdated,
      timeClick,
      endClick,
      onClose,
      handleBlockForSession,
    };
  },
});
